import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import {
  DataGridPro, GridApiPro, GridColDef, useGridApiRef,
} from '@mui/x-data-grid-pro';
import startCase from 'lodash/startCase';
import { MutableRefObject } from 'react';
import { MemoizedBaseAvatar } from '@mgm/common/components/base';
import { dateFormatTZ } from '@mgm/common/utils';
import { useAppSelector } from '../../../hooks/useAppStore';
import { getDashboardData } from '../../../store/dashboard/selectors';
import { TLocation } from '../../../types';
import { useTable } from '../../../hooks/useTable';
import { useUserLocations } from '../../../hooks/useCommon';
import { getPlatformData } from '../../../store/common/selectors';
import { DEFAULT_CURRENCY } from '../../../data';
import { getMembers } from '../../../store/team/selectors';

const DashboardLatestProducts = () => {
  const { latestOrders } = useAppSelector(getDashboardData);
  const apiRef = useGridApiRef() as MutableRefObject<GridApiPro>;
  const { userLocations } = useUserLocations();
  const { currencies } = useAppSelector(getPlatformData);

  const { t } = useTranslation(['inputs', 'dashboard', 'timesheets']);
  const { localText } = useTable(apiRef);
  const members = useAppSelector(getMembers);

  const productsColumns: Array<GridColDef> = [
    {
      field: 'name',
      headerName: t('seller', { ns: 'dashboard' }),
      width: 200,
      renderCell: ({ row }) => {
        const member = members.find((m) => String(m.shopifyId) === row.sellerName);
        return (
          <Box display="flex" alignItems="center" height="100%">
            <MemoizedBaseAvatar avatarFile={member?.avatarFile || null} avatarName={member?.name || ''} />
            <Typography variant="caption">
              {member?.name ? startCase(member?.name) : row.sellerName}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'product',
      headerName: t('product', { ns: 'dashboard' }),
      width: 130,
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="center" height="100%">
          <Typography variant="caption">
            {row.productName || '/'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'location',
      headerName: t('location'),
      minWidth: 130,
      renderCell: ({ row }) => {
        const location = userLocations?.find((location: TLocation) => location.id === row.locationName);

        return (
          <Box display="flex" alignItems="center" height="100%">
            <Typography variant="caption">
              {location?.name || '/'}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'amount',
      headerName: t('amount', { ns: 'timesheets' }),
      minWidth: 130,
      renderCell: ({ row }) => {
        const currency = currencies.find((cr) => cr.code === row.currency) || DEFAULT_CURRENCY;

        return (
          <Box display="flex" alignItems="center" height="100%">
            <Typography variant="caption">
              {`${row?.amount}${currency?.mark}` || '/'}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'date',
      headerName: t('date', { ns: 'timesheets' }),
      minWidth: 170,
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="center" height="100%">
          <Typography variant="caption">
            {dateFormatTZ(row.date, 'DD MMMM, YYYY')}
          </Typography>
        </Box>
      ),
    },
  ];

  return (
    <Card sx={{ height: 500, pb: 9 }}>
      <CardHeader
        sx={{
          pb: 2,
        }}
        title={t('latestProducts', { ns: 'dashboard' })}
      />
      <DataGridPro
        apiRef={apiRef}
        rows={latestOrders || []}
        localeText={localText}
        sx={{
          px: 0,
          '.MuiDataGrid-main > *:first-of-type': {
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
          },
        }}
        slotProps={{
          toolbar: {
            showQuickFilter: true,
            printOptions: {
              hideFooter: true,
              hideToolbar: true,
            },
          },
        }}
        getRowId={(row) => row?.orderId}
        columns={productsColumns}
        hideFooterPagination
        disableRowSelectionOnClick
        hideFooter
        disableColumnMenu
        checkboxSelection={false}
      />
    </Card>
  );
};

export {
  DashboardLatestProducts,
};
